import { API_BASE_URL, ApiService } from "./api";
import { Message, FlightProfile, RouteAnalysis, RouteWaypoint, RouteType, UserProfile, GearRecommendation, RoutePlan, Route, PlanningRoute, FlightChatResponse, PilotHistoricalDossier } from "../types";
import { Language } from "../translations";
import type { EliteFlightAnalysisResult } from "./flightAnalysisEngine";

// Helper to keep types consistent
async function callApi(action: string, body: any) {
  // Authentication
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');

  // API_BASE_URL already contains '/api', so we just append '/ai'
  const response = await fetch(`${API_BASE_URL}/ai?action=${action}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} ${errorText}`);
  }

  const data = await response.json();

  // Async background token refresh to keep the top bar real-time
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => {
        if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user }));
      })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);

  return data;
}

export const getFlightAnalysis = async (date: string, location: string, profile: FlightProfile, targetType: RouteType, lang: Language = 'de', userId?: string, country?: string): Promise<RouteAnalysis> => {
  const data = await callApi('analysis', { date, location, profile, targetType, lang, userId, country });
  return data;
};

// v2.0 AI Planning
export const getMapPlanV2 = async (date: string, location: any, profile: FlightProfile, targetType: RouteType): Promise<any> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/v2/planning/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ date, location, profile, targetType })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Planning Error: ${response.status} ${errorText}`);
  }
  const data = await response.json();
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => { if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user })); })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);
  return data;
};

// v3.0 Multi-Step AI Planning Pipeline
export const getRoutePlanV3 = async (
  date: string,
  profile: FlightProfile,
  targetType: RouteType,
  country: string = 'ch',
  takeoffId?: string | null
): Promise<RoutePlan> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/v3/plan`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      date,
      country,
      targetType,
      profile,
      takeoff_id: takeoffId || null
    })
  });

  if (!response.ok) {
    const errorText = await response.text();

    // 422 = "No routes calculable" — this is NOT an error, just no results for that date/location
    if (response.status === 422) {
      try {
        const info = JSON.parse(errorText);
        return {
          noRoutes: true,
          message: info.error || 'Keine Routen berechenbar',
          hint: info.hint || '',
          details: info.details || ''
        };
      } catch {
        return { noRoutes: true, message: errorText, hint: '', details: '' };
      }
    }

    throw new Error(`V3 Planning Error: ${response.status} ${errorText}`);
  }
  const data = await response.json();
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => { if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user })); })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);
  return data;
};


// New Critical Analysis & Comparison
export const getCriticalAnalysis = async (routeSummary: any, userProfile: any, userId?: string) => {
  return callApi('analyze-route', { action: 'critical', routeSummary, userProfile, userId });
};

export const getRouteComparison = async (routes: any[], userProfile: any, userId?: string) => {
  return callApi('analyze-route', { action: 'compare', routes, userProfile, userId });
};

// AI Variant Comparison - New Feature
export const compareVariants = async (variants: any[], userProfile: any, remarks?: string, includeMeteo: boolean = true, date?: string) => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/ai/compare-variants`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ variants, userProfile, remarks, includeMeteo, date })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Variant AI Error: ${response.status} ${errorText}`);
  }
  const data = await response.json();
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => { if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user })); })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);
  return data;
};

const fetchHistoricalWeather = async (lat: number, lng: number, dateStr: string): Promise<string | null> => {
  try {
    // Basic date validation (YYYY-MM-DD)
    const dateQuery = dateStr.split("T")[0];
    if (!dateQuery.match(/^\d{4}-\d{2}-\d{2}$/)) return null;

    const hourlyParams = 'temperature_2m,relative_humidity_2m,dew_point_2m,precipitation,rain,wind_speed_10m,wind_speed_100m,wind_direction_10m,wind_direction_100m,wind_speed_850hPa,wind_direction_850hPa,wind_speed_700hPa,wind_direction_700hPa,windspeed_850hPa,winddirection_850hPa,windspeed_700hPa,winddirection_700hPa,boundary_layer_height,cape,surface_pressure';
    const url = `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lng}&start_date=${dateQuery}&end_date=${dateQuery}&hourly=${hourlyParams}&timezone=auto`;

    let res = await fetch(url);
    if (!res.ok) {
      const forecastUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&start_date=${dateQuery}&end_date=${dateQuery}&hourly=${hourlyParams}&timezone=auto`;
      res = await fetch(forecastUrl);
      if (!res.ok) return null;
    }

    const data = await res.json();
    const hourly = data.hourly;
    if (!hourly || !hourly.time) return null;

    // Extract relevant hours (11:00 - 17:00) for flying window
    const indices = hourly.time.map((t: string, i: number) => {
      const h = parseInt(t.split("T")[1].split(":")[0]);
      return (h >= 11 && h <= 17) ? i : -1;
    }).filter((i: number) => i !== -1);

    if (indices.length === 0) return null;

    let weatherSummary = `VERIFIZIERTE WETTERDATEN (Open-Meteo) für ${dateQuery} bei ${lat.toFixed(4)}°N, ${lng.toFixed(4)}°E:\n`;
    weatherSummary += `[METEO-HINWEIS: Thermische Grenzschichthöhe (BL) ist ein globaler Modellwert; an sonnigen Hängen liegt die Wolkenbasis im Alpenraum 1000m-2500m höher.]\n`;
    indices.forEach((i: number) => {
      const time = hourly.time[i].split("T")[1];
      const w10s = hourly.wind_speed_10m?.[i] ?? hourly.windspeed_10m?.[i] ?? '?';
      const w10d = hourly.wind_direction_10m?.[i] ?? hourly.winddirection_10m?.[i] ?? '?';
      const w850s = hourly.wind_speed_850hPa?.[i] ?? hourly.windspeed_850hPa?.[i] ?? '?';
      const w850d = hourly.wind_direction_850hPa?.[i] ?? hourly.winddirection_850hPa?.[i] ?? '?';
      const w700s = hourly.wind_speed_700hPa?.[i] ?? hourly.windspeed_700hPa?.[i] ?? '?';
      const w700d = hourly.wind_direction_700hPa?.[i] ?? hourly.winddirection_700hPa?.[i] ?? '?';
      
      weatherSummary += `- ${time} Uhr: Temp ${hourly.temperature_2m?.[i] ?? '?'}°C | Wind 10m: ${w10s} km/h (${w10d}°) | 850hPa (~1500m): ${w850s} km/h (${w850d}°) | 700hPa (~3000m): ${w700s} km/h (${w700d}°) | BL-Höhe: ${hourly.boundary_layer_height?.[i] ?? '?'}m | CAPE: ${hourly.cape?.[i] ?? '?'}\n`;
    });

    return weatherSummary;

  } catch (e) {
    console.warn("Failed to fetch historical weather:", e);
    return null;
  }
};

export const getXCAdvice = async (userPrompt: string, history: Message[], currentRoute: RouteWaypoint[], lang: Language = 'de', flownTrack?: { track: [number, number][], date: string, igcData?: any } | null, userId?: string, profile?: FlightProfile | null) => {
  let finalPrompt = userPrompt;

  // Frontend Injection: Append Track Data directly to the prompt
  if (flownTrack) {
    let contextString = "";

    if (flownTrack.igcData && flownTrack.igcData.fixes && flownTrack.igcData.fixes.length > 0) {
      // RICH MODE: We have the full IGC object
      const igc = flownTrack.igcData;
      const fixes = igc.fixes;

      // Stats
      const formatTimestampToUTCString = (timestamp: number | string) => {
          if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
              const d = new Date(Number(timestamp) * 1000);
              return d.toISOString().split('T')[1].slice(0, 8);
          }
          const tStr = String(timestamp);
          if (tStr.length === 6 && !tStr.includes(':')) {
              return `${tStr.slice(0, 2)}:${tStr.slice(2, 4)}:${tStr.slice(4, 6)}`;
          }
          return tStr;
      };

      const getSwissLocalTime = (utcTimeStr: string, dateStr: string): string => {
          try {
              let formattedTime = utcTimeStr;
              if (utcTimeStr.length === 6 && !utcTimeStr.includes(':')) {
                  formattedTime = `${utcTimeStr.slice(0, 2)}:${utcTimeStr.slice(2, 4)}:${utcTimeStr.slice(4, 6)}`;
              }
              const isoString = `${dateStr}T${formattedTime}Z`;
              const d = new Date(isoString);
              const options: Intl.DateTimeFormatOptions = {
                  timeZone: 'Europe/Zurich',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false
              };
              const formatter = new Intl.DateTimeFormat('de-CH', options);
              return formatter.format(d) + ' Uhr';
          } catch (e) {
              return utcTimeStr + ' (Lokalzeit)';
          }
      };

      const startTimeUTC = formatTimestampToUTCString(fixes[0].time || fixes[0].timestamp);
      const endTimeUTC = formatTimestampToUTCString(fixes[fixes.length - 1].time || fixes[fixes.length - 1].timestamp);
      const startTimeLocal = getSwissLocalTime(startTimeUTC, flownTrack.date);
      const endTimeLocal = getSwissLocalTime(endTimeUTC, flownTrack.date);

      const pilot = igc.pilot || (profile as any)?.pilotName || "Pilot";
      const glider = igc.gliderType || (profile as any)?.glider || (profile as any)?.gliderClass || "Paraglider";

      // Downsample for 3D Path (Lat, Lng, Alt, Time)
      const step = Math.max(1, Math.floor(fixes.length / 50)); // ~50 points for fast, reliable analysis
      const sampled = fixes.filter((_: any, i: number) => i % step === 0).map((f: any) => ({
        lat: (f.lat ?? f.latitude)?.toFixed?.(5) ?? '0',
        lng: (f.lng ?? f.longitude)?.toFixed?.(5) ?? '0',
        alt: f.pressureAltitude || f.gpsAltitude || f.alt || 0,
        t: f.time || f.timestamp || 0
      }));

      // Fetch Historical Weather with vertical wind profile
      let weatherContext = "";
      try {
        const startFix = sampled[0];
        const weather = await fetchHistoricalWeather(parseFloat(startFix.lat), parseFloat(startFix.lng), flownTrack.date);
        if (weather) {
          weatherContext = `\nVERIFIZIERTE WETTERDATEN MIT HÖHENWIND (Open-Meteo):\n${weather}\n(Nutze diese verifizierten Werte als verbindliche Grundlage.)\n`;
        }
      } catch (err) {
        console.log("Weather fetch failed", err);
      }

      contextString = `
[SYSTEM INJECTION - MASTER FLUGANALYSE IGC-DATEN]
Der Pilot hat einen echten IGC-Track hochgeladen.

Metadaten:
- Datum: ${flownTrack.date}
- Pilot: ${pilot}
- Schirm: ${glider}
- Startzeit: ${startTimeUTC} UTC (Schweizer Lokalzeit: ${startTimeLocal})
- Landezeit: ${endTimeUTC} UTC (Schweizer Lokalzeit: ${endTimeLocal})
${weatherContext}

3D Track Sample (Lat, Lng, Alt, Time):
${JSON.stringify(sampled)}

STRIKTE ANFORDERUNGEN & FLOSKELVERBOT FÜR DEINE ANALYSE:
1. ABSOLUTES FLOSKELVERBOT: Verwende NIEMALS allgemeine KI-Phrasen wie "Achte auf den Wind", "Das war ein schöner Flug", "Bereite dich vor", "Passe deine Geschwindigkeit an", "Fliege vorausschauend", "Nutze die Thermik".
2. STRUKTURIERE DEINE ANALYSE IN DEN 5 KERNBEREICHEN:
   - 1. Thermik-Effizienz & Zentrieren: Zentrierzeit, Drehrichtungsvergleich (Rechts vs. Links in m/s), Drift-Kompensation gegen den Höhenwind.
   - 2. Taktische Linienwahl & Talquerungen: Gleitzahl über Grund, Hangabstand, Anfliegen von Prallhängen.
   - 3. MacCready & Sollfahrt: Geschwindigkeitsdelta Delta v zur Polare, Zeit- und Höhenverlust im Sinken/Gegenwind.
   - 4. Meteo-Feinstrukturen: 850hPa vs 700hPa Windscherung, alpine Talwindsysteme und Lee-Zonen.
   - 5. Höhen- & Zeitmanagement: Ausnutzung des 7h-Tagesfensters, Startzeit-Reserve, Low-Saves (AGL).
3. MAP-MARKER FORMAT: Jeder genannte Ort, jede Thermik, jedes Lee MUSS zwingend im Format \`[MAP: lat, lng, "Label"]\` markiert werden.
4. DIREKTE ANSPRACHE: Sprich mich (den Piloten) IMMER direkt mit "Du", "Dein", "Dir" an (niemals 3. Person).
5. ZEITANGABEN: Gib alle Zeiten AUSSCHLIESSLICH in mitteleuropäischer Lokalzeit (Schweiz) an.
6. GEOGRAFIE: Nenne Orte und Pässe nur, wenn der GPS-Track maximal 2 km daran vorbeiführt.
---------------------------------------------
`;

    } else if (flownTrack.track && flownTrack.track.length > 0) {
      // FALLBACK MODE: Only Lat/Lng available
      const step = Math.max(1, Math.floor(flownTrack.track.length / 80));
      const sampled = flownTrack.track.filter((_: any, i: number) => i % step === 0).map((p: any) => {
        if (Array.isArray(p)) {
          return `[${p[0]?.toFixed?.(5) ?? p[0]},${p[1]?.toFixed?.(5) ?? p[1]}]`;
        }
        const lat = p.lat ?? p.latitude ?? 0;
        const lng = p.lng ?? p.longitude ?? 0;
        return `[${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}]`;
      });

      const firstPt = flownTrack.track[0] as any;
      const firstLat = Array.isArray(firstPt) ? firstPt[0] : (firstPt?.lat ?? firstPt?.latitude ?? 0);
      const firstLng = Array.isArray(firstPt) ? firstPt[1] : (firstPt?.lng ?? firstPt?.longitude ?? 0);

      let weatherContext = "";
      if (flownTrack.date && firstLat && firstLng) {
        try {
          const weather = await fetchHistoricalWeather(firstLat, firstLng, flownTrack.date);
          if (weather) weatherContext = `\nVERIFIZIERTE WETTERDATEN (Open-Meteo):\n${weather}\n`;
        } catch (e) { }
      }

      contextString = `
[SYSTEM INJECTION - FLUGDATEN]
Flugdatum: ${flownTrack.date}
${weatherContext}

Track-Punkte:
${JSON.stringify(sampled)}
---------------------------------------------
STRIKTE REGELN (FLOSKELVERBOT):
1. Keine allgemeinen Füllsätze ("Achte auf Wind", "Guter Flug"). Sei quantitativ und direkt.
2. Analysiere Thermik-Zentrieren, Linienwahl, Sollfahrt, Meteo und Zeitmanagement mit konkreten Zahlen.
3. Sprich mich direkt mit "Du" an.
4. Alle erwähnten Orte müssen mit Map-Tags \`[MAP: lat, lng, "Label"]\` versehen werden.
`;
    }

    finalPrompt += contextString;
  }

  // Send track details + analysis flags to the backend
  const data = await callApi('chat', {
    userPrompt: finalPrompt,
    history,
    currentRoute,
    lang,
    flownTrack: flownTrack ? (flownTrack.igcData?.fixes || flownTrack.track) : undefined,
    isFlightAnalysis: flownTrack ? true : false,
    flownTrackDate: flownTrack ? flownTrack.date : undefined,
    userId,
    profile: profile || undefined
  });
  
  // Extract map actions
  if (data && data.text) {
    const mapRegex = /`?\[MAP:\s*([-\d.]+),\s*([-\d.]+),\s*\\?["'](.*?)\\?["']\]`?/g;
    const actions: { lat: number, lng: number, label: string }[] = [];
    
    data.text = data.text.replace(mapRegex, (match: string, latStr: string, lngStr: string, label: string) => {
      const cleanLabel = label.replace(/\\"/g, '"').replace(/\\'/g, "'").trim();
      actions.push({ lat: parseFloat(latStr), lng: parseFloat(lngStr), label: cleanLabel });
      return ""; // Remove the tag from the visible text
    });
    
    if (actions.length > 0) {
      data.mapActions = actions;
    }
  }

  return data;
};

export const getGearRecommendation = async (p: UserProfile, userId?: string): Promise<GearRecommendation> => {
  const data = await callApi('gear', { profile: p, userId });
  return data;
};
// Sales Chat
export const getSalesAdvice = async (userPrompt: string, history: Message[], quickTips?: any[]) => {
  const data = await callApi('sales', { userPrompt, history, quickTips });
  return data;
};

// Weather AI Chat
export const getWeatherAdvice = async (userPrompt: string, history: Message[], weatherData: any, selectedDay: string, lang: Language = 'de', userId?: string, profile?: FlightProfile | null) => {
  // Inject weather context invisibly into the prompt
  const contextString = `
[SYSTEM INJECTION - WEATHER DATA CONTEXT]
The user is looking at the Weather AI Map for ${selectedDay}.
Here is a summary of the regions and their weather potential for this day:
${weatherData ? JSON.stringify(weatherData).substring(0, 3000) + '... (truncated)' : 'No data available.'}

TASK:
1. Answer the user's question about the flight weather for ${selectedDay} based ONLY on the data above.
2. If the user asks about a specific region, look it up in the data.
3. EXTREMELY IMPORTANT: Answer DIRECTLY without any pleasantries, introductions, or fluff. DO NOT start with "Das ist eine hervorragende Frage" or similar.
4. DO NOT explain basic concepts unless explicitly asked. Just give the weather facts and your assessment. Be as concise as possible.
5. Keep token usage extremely low. Give facts, not a novel.
---------------------------------------------
`;
  
  const finalPrompt = userPrompt + contextString;
  const data = await callApi('chat', { userPrompt: finalPrompt, history, currentRoute: [], lang, userId, profile: profile || undefined });
  return data;
};

// Elite Master Flight Analysis API
export const analyzeFlightWithEngine = async (params: {
  igc_content?: string;
  track_id?: string;
  userProfile?: any;
  pilot_profile?: any;
  macCready?: number;
  gliderClass?: string;
  glideRatio?: number;
}): Promise<EliteFlightAnalysisResult & { id?: string; tokens_cost?: number }> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/flight-analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Flight Analysis Error: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => { if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user })); })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);
  return data;
};

// AI Flight Analysis Executive Coaching (Strict Anti-Floskel)
export const getAiFlightAnalysis = async (params: {
  analysisResult?: EliteFlightAnalysisResult;
  track_id?: string;
  igc_content?: string;
  pilotProfile?: any;
}): Promise<any> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE_URL}/ai/flight-analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(params)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI Flight Analysis Error: ${response.status} ${errorText}`);
  }

  const data = await response.json();
  setTimeout(() => {
    ApiService.getProfile()
      .then(user => { if (user) window.dispatchEvent(new CustomEvent('user-refresh', { detail: user })); })
      .catch((e) => console.warn('Background token refresh failed:', e));
  }, 100);
  return data;
};

// Task Comparison & AI Coach (Milestone 3)
export const getTaskComparison = async (
  flightId: string,
  forceRecalculate = false,
  cylinderRadiusM = 400,
  gliderClass = 'EN-B'
): Promise<any> => {
  return ApiService.getTaskComparison(flightId, forceRecalculate, cylinderRadiusM, gliderClass);
};

export const generateTaskCoaching = async (
  flightId: string,
  forceRegenerate = false,
  pilotProfile?: any
): Promise<any> => {
  return ApiService.generateTaskCoaching(flightId, forceRegenerate, pilotProfile);
};

// ====================================================================================================
// Interactive AI Flight Coach & Historical Debriefing (R1/R2)
// ====================================================================================================

/**
 * Interaktiver KI-Flug-Coach & Debriefing für geflogene Tracks (R1/R2)
 */
export const getInteractiveFlightAdvice = async (
  flightId: string | number,
  userPrompt: string,
  history: Array<{ role: string; text: string; timestamp?: string | number; quickPromptType?: string; mapActions?: any }>,
  analysisData?: any,
  currentWeather?: any,
  trackMeta?: { pilotName?: string; date?: string; distanceKm?: number; glider?: string; [key: string]: any },
  quickPromptType?: string,
  taskComparisonData?: any
): Promise<FlightChatResponse> => {
  const data = await callApi('flight_chat', {
    flightId,
    userPrompt,
    history,
    analysisData,
    currentWeather,
    trackMeta,
    quickPromptType,
    taskComparisonData
  });

  // Extract map actions if not already parsed on backend
  if (data && data.text && !data.mapActions) {
    const mapRegex = /`?\[MAP:\s*([-\d.]+),\s*([-\d.]+),\s*\\?["'](.*?)\\?["']\]`?/g;
    const actions: { lat: number; lng: number; label: string }[] = [];

    data.text = data.text.replace(mapRegex, (match: string, latStr: string, lngStr: string, label: string) => {
      const cleanLabel = label.replace(/\\"/g, '"').replace(/\\'/g, "'").trim();
      actions.push({ lat: parseFloat(latStr), lng: parseFloat(lngStr), label: cleanLabel });
      return `📍 **${cleanLabel}**`;
    });

    if (actions.length > 0) {
      data.mapActions = actions;
    }
  }

  return data;
};

/**
 * Loads persistent chat history and historical dossier for a flight.
 */
export const getFlightChatHistory = async (
  flightId: string | number
): Promise<{ flightId: string | number; chat_history: any[]; historicalDossier?: PilotHistoricalDossier }> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  try {
    const response = await fetch(`${API_BASE_URL}/flight-chat/${flightId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.warn('[FlightChat] History endpoint error:', e);
  }

  // Fallback: try loading track directly
  try {
    const trackRes = await fetch(`${API_BASE_URL}/data/flight-tracks/${flightId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (trackRes.ok) {
      const trackData = await trackRes.json();
      return {
        flightId,
        chat_history: trackData.chat_history || []
      };
    }
  } catch { }

  return { flightId, chat_history: [] };
};

/**
 * Clears persistent chat history for a flight.
 */
export const clearFlightChatHistory = async (flightId: string | number): Promise<boolean> => {
  const token = localStorage.getItem('xc_auth_token') || localStorage.getItem('auth_token');
  try {
    const response = await fetch(`${API_BASE_URL}/flight-chat/${flightId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    return response.ok;
  } catch {
    return false;
  }
};


